import { Button } from '@/shared/ui/button';
import {
  addDays,
  addMonths,
  getDaysInMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import type { GanttZoomLevel, StepDirection } from './types';

interface GanttViewControlsProps {
  zoomLevel: GanttZoomLevel;
  onZoomChange: (zoom: GanttZoomLevel) => void;
}

const ZOOM_OPTIONS: { value: GanttZoomLevel; label: string }[] = [
  { value: 'hour', label: 'Hour' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

export function GanttViewControls({ zoomLevel, onZoomChange }: GanttViewControlsProps) {
  return (
    <div className="flex items-center border border-border rounded-md overflow-hidden">
      {ZOOM_OPTIONS.map((option) => (
        <Button
          key={option.value}
          variant="ghost"
          size="sm"
          onClick={() => onZoomChange(option.value)}
          className={`rounded-none h-8 px-3 ${
            zoomLevel === option.value
              ? 'bg-[#E84A2B] text-white hover:bg-[#d43d20] hover:text-white'
              : ''
          }`}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}

/**
 * Derive the visible span (in days) from the current zoom level.
 * Month uses the actual number of days in `cursor`'s month so February
 * isn't padded out to 31.
 */
export function ganttDaysToShow(zoomLevel: GanttZoomLevel, cursor: Date): number {
  switch (zoomLevel) {
    case 'hour':
    case 'day':
      return 1;
    case 'week':
      return 7;
    case 'month':
      return getDaysInMonth(cursor);
  }
}

/**
 * Snap the chart's left edge based on zoom level so Week starts on Monday
 * and Month starts on the 1st.
 */
export function ganttRangeStart(zoomLevel: GanttZoomLevel, cursor: Date): Date {
  switch (zoomLevel) {
    case 'week':
      return startOfWeek(cursor, { weekStartsOn: 1 });
    case 'month':
      return startOfMonth(cursor);
    case 'hour':
    case 'day':
    default:
      return startOfDay(cursor);
  }
}

/**
 * Move the navigation cursor by one "unit" of the active zoom.
 *  - hour/day: ±1 day
 *  - week: ±7 days (rangeStart will re-snap to Monday)
 *  - month: ±1 calendar month
 */
export function ganttStepDate(
  zoomLevel: GanttZoomLevel,
  cursor: Date,
  direction: StepDirection,
): Date {
  switch (zoomLevel) {
    case 'hour':
    case 'day':
      return addDays(cursor, direction);
    case 'week':
      return addDays(cursor, direction * 7);
    case 'month':
      return addMonths(cursor, direction);
  }
}
